import * as React from "react";
import { ElementHtmlAttributes, getAttributes } from "../common/common";
import { Align, DataActionDecorate, IconPosition, K2Boolean, UpdateCommandItem, UpdateControl } from "../common/communication.base";
import { NclButton, NclImage, NclVirtualKeyboardDialog } from "../common/components.ncl";
import { DecorateResult } from "../common/visualContext";
import { SubMenuIndicator } from "./K2Action";
import { K2ComponentState, withContext, WithContextPlacementProps, StyleHelper, AcquireControl } from "./k2hoc";
import { K2Img } from "./K2Image";
import { K2RuleWithVCXProps } from "./k2StyleRenderer";
import { K2TruncateText } from "./K2TruncateText";

type ButtonProps = K2RuleWithVCXProps & {
  fillWidth: K2Boolean;
  decorate: DecorateResult;
  disabled: boolean;
};

const styleRules = {
  buttonDecorateRule: (props: ButtonProps) => ({
    background: props.decorate.backColor ? props.decorate.backColor : "unset",
    color: props.decorate.foregroundColor ? props.decorate.foregroundColor : "unset",
    fontWeight: props.decorate.bold ? ("bold" as const) : ("normal" as const),
    position: "relative" as const,
    flex: props.fillWidth ? "1 1 auto" : "0 1 auto",
    opacity: props.disabled ? "0.4" : null,
    cursor: props.disabled ? "not-allowed" : null,
  }),
  padding: (props: K2RuleWithVCXProps & { isVirtualKeyboardButton: boolean }) => ({
    flex: "1 1 auto",
    padding: props.isVirtualKeyboardButton ? "1.5px" : null,
  }),
  iconLeftRight: () => ({
    alignItems: "center",
  }),
  iconTopBottom: () => ({
    flex: "1 1 auto",
    flexDirection: "column" as const,
  }),
  textLeftRight: (props: K2RuleWithVCXProps & { align: Align }) => ({
    flex: props.align === Align.Left || props.align === Align.Right ? "0 0 auto" : "1 1 auto",
    justifyContent: "center",
    alignItems: "center",
  }),
  center: () => ({
    justifyContent: "center",
    alignItems: "center",
    flex: "1 1 auto",
  }),
  stretch: () => ({
    justifyContent: "center",
    flex: "0 0 auto",
  }),
};

export type BeforeExecuteProc = (control: NclButton) => void;

interface K2ButtonProps extends WithContextPlacementProps {
  beforeExecute?: BeforeExecuteProc;
  id?: string;
  fillSvg?: boolean; //only for client button in virtual keyboard
}

class _Button extends React.PureComponent<K2ButtonProps, K2ComponentState<UpdateCommandItem>> {
  static displayName = "K2Button";
  private title: K2TruncateText;
  private control: NclButton;
  private element: HTMLDivElement;

  constructor(props: K2ButtonProps) {
    super(props);
    this.control = AcquireControl(this.props.controlUID, this.props.vrUID, (ctrl) => ctrl instanceof NclButton) as NclButton;
    this.state = { data: this.control.init(this) as UpdateCommandItem, vcxVersion: -1 };
  }

  setAsActive(isActive: boolean) {
    if (this.element && isActive) {
      this.element.focus({ preventScroll: true });
    }
  }

  updateState(state: UpdateControl) {
    this.setState(() => {
      return { data: state as UpdateCommandItem };
    });
  }

  updateVCX(vcxVersion: number) {
    this.setState({ vcxVersion: vcxVersion });
  }

  componentWillUnmount() {
    this.control.willUnMount(true);
    this.control = null;
  }

  render() {
    const addAttributes: ElementHtmlAttributes = getAttributes(this.state.data);

    let finId: string = this.control.MetaData.Name;
    if (this.props.id != "" && this.props.id != undefined) {
      finId = this.props.id + finId;
    }

    const actDec = this.getActualDecorate();

    return (
      <div style={StyleHelper(this.control, { ...this.props.style })}>
        <div
          className={this.props.renderer.renderRule(styleRules.padding, {
            vcx: this.control.VCX,
            isVirtualKeyboardButton: this.control.Parent instanceof NclVirtualKeyboardDialog,
          })}
        >
          <div
            style={{
              padding:
                this.control.MetaData.Bounds.Align === Align.Left || this.control.MetaData.Bounds.Align === Align.Right
                  ? `0px ${this.control.VCX.sizeMap(6)}px`
                  : null,
              width: this.control.Ncl.FrgtData.FillWidth ? null : this.control.ComputedMinHeightWithMargin + "px",
            }}
            tabIndex={this.state.data.TabStop ? 0 : -1}
            data-k2-test-id={finId}
            ref={(ref) => (this.element = ref)}
            className={
              "button " +
              this.props.renderer.renderRule(styleRules.buttonDecorateRule, {
                vcx: this.control.VCX,
                decorate: this.getActualDecorate(),
                fillWidth: this.control.Ncl.FrgtData.FillWidth,
                disabled: !this.state.data.Enabled,
              })
            }
            onClick={this.handleClick}
            onFocus={this.handleFocus}
            onKeyDown={this.handleKeyDown}
            accessKey={this.title ? this.title.accessKey : null}
            {...addAttributes}
          >
            {this.getContent(actDec)}
            {this.state.data.SubMenuIndicatorVisible && <SubMenuIndicator vcx={this.control.VCX} strokeColor={actDec.foregroundColor} />}
          </div>
        </div>
      </div>
    );
  }

  private getActualDecorate(): DecorateResult {
    let result: DecorateResult;
    if (this.state.data.Enabled === true) {
      result = this.control.VCX.ColorMap.getColorsForActionDecorate(
        this.state.data.Decorate ? this.state.data.Decorate : this.control.Ncl.FrgtData.Decorate,
        this.state.data.Enabled
      );

      if (this.control.Ncl.BackgroundColorCalc > 0) {
        result.backColor = this.control.VCX.getColor(this.control.Ncl.BackgroundColorCalc);
      }

      if (this.control.Ncl.ForegroundColorCalc > 0) {
        result.foregroundColor = this.control.VCX.getColor(this.control.Ncl.ForegroundColorCalc);
      }
    } else {
      result = this.control.VCX.ColorMap.getColorsForActionDecorate(DataActionDecorate.dadeContentSuppress, this.state.data.Enabled);
    }
    return result;
  }

  private getContent(actDec: DecorateResult): JSX.Element {
    if (!this.state.data.GlyphId || this.control.Ncl.FrgtData.ShowIcon === 0) {
      return this.control.Ncl.FrgtData.ShowCaption ? (
        <div className={this.props.renderer.renderRule(styleRules.center, { vcx: this.control.VCX })} key={this.control.Ncl.ControlUID + "_txt"}>
          <K2TruncateText
            ref={(ref) => {
              this.title = ref;
            }}
            className={this.props.renderer.renderFontRule(this.control.VCX.LabelControl.Font, this.control.VCX)}
          >
            {this.state.data.Title}
          </K2TruncateText>
        </div>
      ) : null;
    } else {
      if (!this.control.Ncl.FrgtData.ShowCaption) {
        return (
          <K2Img
            glyphId={this.state.data.GlyphId}
            backgroundColor={actDec.backColor}
            strokeColor={actDec.foregroundColor}
            vcx={this.control.VCX}
            nonSvgHeight={64}
          />
        );
      }

      switch (this.control.Ncl.FrgtData.IconPosition) {
        case IconPosition.ipBottom:
          return (
            <div className={this.props.renderer.renderRule(styleRules.iconTopBottom, { vcx: this.control.VCX })}>
              <div className={this.props.renderer.renderRule(styleRules.stretch, { vcx: this.control.VCX })}>
                <K2TruncateText
                  ref={(ref) => {
                    this.title = ref;
                  }}
                  className={this.props.renderer.renderFontRule(this.control.VCX.LabelControl.Font, this.control.VCX)}
                >
                  {this.state.data.Title}
                </K2TruncateText>
              </div>
              <K2Img
                glyphId={this.state.data.GlyphId}
                backgroundColor={actDec.backColor}
                strokeColor={actDec.foregroundColor}
                vcx={this.control.VCX}
                nonSvgHeight={64}
              />
            </div>
          );
        case IconPosition.ipTop:
          return (
            <div className={this.props.renderer.renderRule(styleRules.iconTopBottom, { vcx: this.control.VCX })}>
              <K2Img
                glyphId={this.state.data.GlyphId}
                backgroundColor={actDec.backColor}
                strokeColor={actDec.foregroundColor}
                vcx={this.control.VCX}
                nonSvgHeight={64}
              />
              <div className={this.props.renderer.renderRule(styleRules.stretch, { vcx: this.control.VCX })}>
                <K2TruncateText
                  ref={(ref) => {
                    this.title = ref;
                  }}
                  className={this.props.renderer.renderFontRule(this.control.VCX.LabelControl.Font, this.control.VCX)}
                >
                  {this.state.data.Title}
                </K2TruncateText>
              </div>
            </div>
          );
        case IconPosition.ipLeft:
          return (
            <>
              <K2Img
                glyphId={this.state.data.GlyphId}
                backgroundColor={actDec.backColor}
                strokeColor={actDec.foregroundColor}
                vcx={this.control.VCX}
                width={this.element?.offsetHeight}
                nonSvgHeight={64}
              />
              <div className={this.props.renderer.renderRule(styleRules.textLeftRight, { vcx: this.control.VCX, align: this.control.MetaData.Bounds.Align })}>
                <K2TruncateText
                  ref={(ref) => {
                    this.title = ref;
                  }}
                  className={this.props.renderer.renderFontRule(this.control.VCX.LabelControl.Font, this.control.VCX)}
                >
                  {this.state.data.Title}
                </K2TruncateText>
              </div>
            </>
          );
        case IconPosition.ipRight:
          return (
            <>
              <div className={this.props.renderer.renderRule(styleRules.textLeftRight, { vcx: this.control.VCX, align: this.control.MetaData.Bounds.Align })}>
                <K2TruncateText
                  ref={(ref) => {
                    this.title = ref;
                  }}
                  className={this.props.renderer.renderFontRule(this.control.VCX.LabelControl.Font, this.control.VCX)}
                >
                  {this.state.data.Title}
                </K2TruncateText>
              </div>
              <K2Img
                glyphId={this.state.data.GlyphId}
                backgroundColor={actDec.backColor}
                strokeColor={actDec.foregroundColor}
                vcx={this.control.VCX}
                width={this.element?.offsetHeight}
                nonSvgHeight={64}
              />
            </>
          );
        case IconPosition.ipCenter:
          return (
            <>
              <K2Img
                glyphId={this.state.data.GlyphId}
                strokeColor={actDec.foregroundColor}
                vcx={this.control.VCX}
                style={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", width: "100%", height: "100%" }}
                nonSvgHeight={64}
              />
              <div className={this.props.renderer.renderRule(styleRules.center, { vcx: this.control.VCX })}>
                <K2TruncateText
                  ref={(ref) => {
                    this.title = ref;
                  }}
                  className={this.props.renderer.renderFontRule(this.control.VCX.LabelControl.Font, this.control.VCX)}
                >
                  {this.state.data.Title}
                </K2TruncateText>
              </div>
            </>
          );
      }
    }
  }

  private execute() {
    if (this.props.beforeExecute) {
      this.props.beforeExecute.call(this, this.control);
    }

    this.control.executeCommand(null);
  }

  private handleClick = () => {
    this.execute();
  };

  private handleFocus = () => {
    setTimeout(() => {
      if (this.control) this.control.setActiveControlRequested();
    }, 80);
  };

  private handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Enter") {
      this.execute();

      e.stopPropagation();
      e.preventDefault();
    } else if (e.key === "ArrowRight") {
      (this.element.parentElement.parentElement.nextElementSibling?.firstElementChild?.firstElementChild as HTMLDivElement)?.focus();
    } else if (e.key === "ArrowLeft") {
      (this.element.parentElement.parentElement.previousElementSibling?.firstElementChild?.firstElementChild as HTMLDivElement)?.focus();
    }
  };
}

export const K2Button = withContext(_Button);
